import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { customerAPI, auth } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Key, Monitor, ShoppingCart, TrendingUp, Copy, Check } from 'lucide-react';

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    // Check if user is authenticated
    if (!auth.isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Load user data
    const userData = auth.getUser();
    setUser(userData);

    // Load dashboard data
    loadDashboardData();
  }, [navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load stats and licenses in parallel
      const [statsResponse, licensesResponse] = await Promise.all([
        customerAPI.getStats(),
        customerAPI.getLicenses(),
      ]);

      setStats(statsResponse.data);
      setLicenses(licensesResponse.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const copyLicenseKey = (licenseKey, licenseId) => {
    navigator.clipboard.writeText(licenseKey);
    setCopiedKey(licenseId);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-6">
            <Skeleton className="h-12 w-64 bg-purple-800/20" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
            <Skeleton className="h-96" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 pt-20">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-300">Total Licenses</CardTitle>
              <Key className="h-4 w-4 text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{stats?.total_licenses || 0}</div>
              <p className="text-xs text-gray-400">
                {stats?.active_licenses || 0} active
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-300">Active Devices</CardTitle>
              <Monitor className="h-4 w-4 text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{stats?.active_devices || 0}</div>
              <p className="text-xs text-gray-400">
                Across all licenses
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-300">Total Purchases</CardTitle>
              <ShoppingCart className="h-4 w-4 text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{stats?.total_purchases || 0}</div>
              <p className="text-xs text-gray-400">
                All time
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-300">Total Spent</CardTitle>
              <TrendingUp className="h-4 w-4 text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">
                ${(stats?.total_spent || 0).toFixed(2)}
              </div>
              <p className="text-xs text-gray-400">
                Lifetime value
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Licenses Section */}
        <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">Your Licenses</CardTitle>
                <CardDescription className="text-gray-400">Manage your Cogsmith Pro licenses</CardDescription>
              </div>
              <Button asChild className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                <Link to="/pricing">Buy License</Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {licenses.length === 0 ? (
              <div className="text-center py-12">
                <Key className="mx-auto h-12 w-12 text-purple-400/50" />
                <h3 className="mt-2 text-sm font-medium text-white">No licenses</h3>
                <p className="mt-1 text-sm text-gray-400">
                  Get started by purchasing a Cogsmith Pro license.
                </p>
                <div className="mt-6">
                  <Button asChild className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                    <Link to="/pricing">Buy License</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {licenses.map((license) => (
                  <div
                    key={license.id}
                    className="border border-purple-500/20 rounded-lg p-4 hover:bg-purple-500/5 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={license.revoked ? 'destructive' : 'default'} className="bg-purple-600">
                            {license.tier.toUpperCase()}
                          </Badge>
                          {license.revoked && (
                            <Badge variant="destructive">REVOKED</Badge>
                          )}
                          {!license.revoked && !license.expires_at && (
                            <Badge variant="secondary" className="bg-green-600">LIFETIME</Badge>
                          )}
                        </div>

                        <div className="font-mono text-sm bg-gray-800/50 p-2 rounded flex items-center gap-2 mb-2 border border-purple-500/20">
                          <span className="break-all text-gray-300 flex-1 min-w-0">{license.license_key}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyLicenseKey(license.license_key, license.id)}
                            className="hover:bg-purple-500/10 flex-shrink-0"
                          >
                            {copiedKey === license.id ? (
                              <Check className="h-4 w-4 text-green-400" />
                            ) : (
                              <Copy className="h-4 w-4 text-purple-400" />
                            )}
                          </Button>
                        </div>

                        <div className="text-sm text-gray-400 space-y-1">
                          <p>Issued: {formatDate(license.issued_at)}</p>
                          {license.expires_at && (
                            <p>Expires: {formatDate(license.expires_at)}</p>
                          )}
                          <p>
                            Devices: {license.active_devices}/{license.device_limit}
                          </p>
                          {license.revoked && license.revoked_reason && (
                            <p className="text-red-400">
                              Revoked: {license.revoked_reason}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                          className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                        >
                          <Link to={`/dashboard/licenses/${license.id}`}>
                            Manage
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-lg text-white">Purchase History</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-400 mb-4">
                View all your past purchases and download invoices.
              </p>
              <Button variant="outline" asChild className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                <Link to="/dashboard/purchases">View Purchases</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-lg text-white">Download Cogsmith</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-400 mb-4">
                Download the latest version of Cogsmith for Windows.
              </p>
              <Button variant="outline" asChild className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                <Link to="/download">Download</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-lg text-white">Documentation</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-400 mb-4">
                Learn how to use Cogsmith and all its features.
              </p>
              <Button variant="outline" asChild className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                <Link to="/docs">View Docs</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

